import { MAP_BASIC_CONFIGURATION, MARKER_STYLES } from '@/js/helpers/map-view-helper';
import * as L from 'leaflet';
import router from '@/router';
import { addressClient } from '@/js/helpers/backend-clients';
import {
  displayAxiosError,
  displayErrorMessage,
  hideLoadingOverlay,
  showLoadingOverlay,
} from '@/js/helpers/utils';

const defaultState = () => {
  return {
    currentPosition: MAP_BASIC_CONFIGURATION.center,
    currentZoom: MAP_BASIC_CONFIGURATION.zoom,
    currentAddress: undefined,
    alternateAddresses: [],
    foundAddress: undefined,
    markers: new L.FeatureGroup(),
    markerInstance: undefined,
  };
};

const state = defaultState();

const mutations = {
  UPDATE_CURRENT_POSITION(state, currentPosition) {
    state.currentPosition = currentPosition;
  },
  UPDATE_CURRENT_ZOOM(state, currentZoom) {
    state.currentZoom = currentZoom;
  },
  UPDATE_CURRENT_ADDRESS(state, currentAddress) {
    state.currentAddress = currentAddress;
  },
  UPDATE_ALT_ADDRESSES(state, alternateAddresses) {
    state.alternateAddresses = alternateAddresses;
  },
  UPDATE_FOUND_ADDRESS(state, foundAddress) {
    state.foundAddress = foundAddress;
  },
  REPLACE_MARKER(state, marker) {
    state.markers.addLayer(marker);
  },
  SET_MARKER_INSTANCE(state, instance) {
    state.markerInstance = instance;
  },
  RESET_STATE(state) {
    state.markers.clearLayers();
    state.currentPosition = MAP_BASIC_CONFIGURATION.center;
    state.currentZoom = MAP_BASIC_CONFIGURATION.zoom;
    state.currentAddress = undefined;
    state.alternateAddresses = [];
    state.foundAddress = undefined;
    state.markerInstance = undefined;
  },
};

const actions = {
  updateCurrentZoom({ commit }, zoom) {
    commit('UPDATE_CURRENT_ZOOM', zoom);
  },
  updateCurrentAddress({ state, commit }, address) {
    addressClient.updateAdresse().then(() => {
      commit('UPDATE_CURRENT_ADDRESS', address);
    });
  },
  updateCurrentAddressPlz({ state, commit, dispatch }, plz) {
    let address = state.currentAddress;
    if (address) {
      address.plz = plz;
      commit('UPDATE_CURRENT_ADDRESS', address);
    }
  },
  updateCurrentAddressOrt({ state, commit, dispatch }, ort) {
    let address = state.currentAddress;
    if (address) {
      address.ort = ort;
      commit('UPDATE_CURRENT_ADDRESS', address);
    }
  },
  updateCurrentAddressStr({ state, commit, dispatch }, str) {
    let address = state.currentAddress;
    if (address) {
      address.str = str;
      commit('UPDATE_CURRENT_ADDRESS', address);
    }
  },
  updateCurrentAddressHnr({ state, commit, dispatch }, hnr) {
    let address = state.currentAddress;
    if (address) {
      address.hnr = hnr;
      commit('UPDATE_CURRENT_ADDRESS', address);
    }
  },
  clickMap({ state, commit, dispatch }, position) {
    commit('UPDATE_FOUND_ADDRESS', undefined);
    commit('UPDATE_ALT_ADDRESSES', []);
    dispatch('markMap', position);
    return dispatch('loadPositionAddress', position);
  },
  clearAddress({ dispatch }) {
    addressClient.clearAddress().then(() => dispatch('reset'));
  },
  replaceMarker({ state, commit, dispatch }, { position, draggable }) {
    state.markers.clearLayers();
    let marker = new L.marker(position, {
      icon: MARKER_STYLES.BLUE_MARKER,
      draggable: draggable,
    });
    if (draggable) {
      marker.on('dragend', e => {
        commit('UPDATE_CURRENT_POSITION', { ...e.target.getLatLng() });
        dispatch('loadPositionAddress', state.currentPosition);
      });
    }
    commit('SET_MARKER_INSTANCE', marker);
    commit('REPLACE_MARKER', marker);
  },
  markMap({ state, commit, dispatch }, position) {
    if (position) {
      commit('UPDATE_CURRENT_POSITION', position);
      dispatch('replaceMarker', { position: position, draggable: true });
    }
  },
  loadFormatAddress({ state, dispatch }, fct) {
    showLoadingOverlay();
    dispatch('loadAddress', addressClient.address({ ...state.currentAddress })).then(fct);
  },
  loadPositionAddress({ commit, dispatch }, pos) {
    showLoadingOverlay();
    return dispatch('loadAddress', addressClient.addressPosition(pos.lat, pos.lng));
  },
  loadAddress({ dispatch, commit }, promise) {
    return promise
      .then(({ data }) => {
        commit('UPDATE_CURRENT_ADDRESS', data.inputAddress);
        commit('UPDATE_FOUND_ADDRESS', data.address);
        dispatch('markMap', data.coordinates);
        commit('UPDATE_CURRENT_ZOOM', data.zoom);
      })
      .catch(error => {
        displayAxiosError(error);
        if (error.response && (error.response.status === 401 || error.response.status === 401)) {
          router.push('/login').finally();
        }
      })
      .finally(() => {
        hideLoadingOverlay();
      });
  },
  searchAddressQuery({ dispatch, commit }, addressQuery) {
    if (addressQuery && addressQuery !== '') {
      showLoadingOverlay();
      addressClient
        .addressQuery(addressQuery)
        .then(({ data }) => {
          if (data.address.plz || data.address.ort || data.address.str || data.address.hnr) {
            commit('UPDATE_FOUND_ADDRESS', data.address);
            commit('UPDATE_CURRENT_ADDRESS', data.inputAddress);
            dispatch('markMap', data.coordinates);
          } else {
            displayErrorMessage('Die Adresse konnte nicht gefunden werden.');
          }
          commit('UPDATE_CURRENT_ZOOM', data.zoom);
          commit('UPDATE_ALT_ADDRESSES', data.alternateAddresses);
        })
        .catch(error => {
          displayAxiosError(error);
          if (error.response && (error.response.status === 401 || error.response.status === 401)) {
            router.push('/login').finally();
          }
        })
        .finally(() => {
          hideLoadingOverlay();
        });
    }
  },
  reset({ commit }) {
    commit('RESET_STATE');
  },
};

const getters = {
  currentPosition: () => {
    return state.currentPosition;
  },
  currentZoom: () => {
    return state.currentZoom;
  },
  currentAddress: () => {
    return state.currentAddress;
  },
  currentAddressString: () => {
    return state.currentAddress
      ? state.currentAddress.plz.concat(
          ' ',
          state.currentAddress.ort,
          ', ',
          state.currentAddress.str,
          ' ',
          state.currentAddress.hnr,
        )
      : '';
  },
  alternateAddresses: () => {
    return state.alternateAddresses;
  },
  foundAddress: () => {
    return state.foundAddress;
  },
  markers: () => {
    return state.markers;
  },
  markerInstance: () => {
    return state.markerInstance;
  },
};

const addressModule = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};

export default addressModule;
